import React, { useState, useMemo } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

// Tiny sort hook + header cell used across the app so every table header
// can be clicked to sort by that column.
//
// Usage:
//   const { sort, onSort, sortRows } = useSort();
//   const sorted = sortRows(rows, {
//     name: (r) => r.name,
//     cost: (r) => r.cost,
//   });
//
//   <SortableTh sortKey="name" sort={sort} onSort={onSort}>Name</SortableTh>
//
// Sorting cycles: asc → desc → off (original order).

export const useSort = (initial = { key: null, dir: 'asc' }) => {
  const [sort, setSort] = useState(initial);

  const onSort = (key) => {
    setSort((s) => {
      if (s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  };

  const sortRows = (rows, getters) => {
    if (!sort.key || !getters[sort.key]) return rows;
    const get = getters[sort.key];
    const dirMul = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = get(a); const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dirMul;
      }
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * dirMul;
    });
  };

  return { sort, onSort, sortRows };
};

// Re-export useMemo convenience for callers that want stable sorted output
export const useSortedRows = (rows, getters, sort) => useMemo(() => {
  if (!sort.key || !getters[sort.key]) return rows;
  const get = getters[sort.key];
  const dirMul = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = get(a); const vb = get(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * dirMul;
    }
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * dirMul;
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [rows, sort.key, sort.dir]);

export const SortableTh = ({ children, sortKey, sort, onSort, className = '', numeric = false, style, colSpan, rowSpan }) => {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  const iconClass = active ? 'text-[hsl(var(--primary))]' : 'text-[#B0B0A8]';
  const thClass = `${className} cursor-pointer select-none hover:bg-[#E8E8E3]`;
  return (
    <th
      className={thClass + (numeric ? ' num' : '')}
      onClick={() => onSort(sortKey)}
      style={style}
      colSpan={colSpan}
      rowSpan={rowSpan}
      data-testid={`sort-${sortKey}`}
    >
      <span className={`inline-flex items-center gap-1 ${numeric ? 'justify-end w-full' : ''}`}>
        {numeric && <Icon size={10} className={iconClass} />}
        <span>{children}</span>
        {!numeric && <Icon size={10} className={iconClass} />}
      </span>
    </th>
  );
};
